import { IntlShape } from '@formatjs/intl';

type FooterLink = {
  title: string;
  href?: string;
  labels?: {
    expand: string;
    collapse: string;
  };
};
export type FooterColumn = {
  items: FooterLink[];
};

export type FooterLinksOptions = {
  plansUrl?: string;
};

type FooterLinks = {
  columns: FooterColumn[];
};

/**
 * Footer links configuration.
 * Organized as two rows of three columns each.
 */
export const footerLinks = (intl: IntlShape): FooterLinks => ({
  columns: [
    // Row 1
    {
      items: [
        {
          title: intl.formatMessage({
            id: 'footer.column.header.exams',
            defaultMessage: 'Exams',
          }),
          labels: {
            expand: intl.formatMessage({
              id: 'footer.column.button.expand.exams',
              defaultMessage: 'Expand Exams',
            }),
            collapse: intl.formatMessage({
              id: 'footer.column.button.collapse.exams',
              defaultMessage: 'Collapse Exams',
            }),
          },
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.usmle',
            defaultMessage: 'USMLE 1+2',
          }),
          href: '/exam-prep/usmle/',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.comlex',
            defaultMessage: 'COMLEX 1+2',
          }),
          href: '/exam-prep/comlex-usa/',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.pance',
            defaultMessage: 'PANCE',
          }),
          href: '/exam-prep/pance/',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.nclex',
            defaultMessage: 'NCLEX',
          }),
          href: '/exam-prep/nclex/',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.ukmla',
            defaultMessage: 'UKMLA',
          }),
          href: '/exam-prep/ukmla/',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.abfm-abim',
            defaultMessage: 'ABFM/ABIM',
          }),
          href: '/exam-prep/abfm-abim/',
        },
      ],
    },
    {
      items: [
        {
          title: intl.formatMessage({
            id: 'footer.column.header.pricing',
            defaultMessage: 'Pricing',
          }),
          labels: {
            expand: intl.formatMessage({
              id: 'footer.column.button.expand.pricing',
              defaultMessage: 'Expand Pricing',
            }),
            collapse: intl.formatMessage({
              id: 'footer.column.button.collapse.pricing',
              defaultMessage: 'Collapse Pricing',
            }),
          },
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.plans-md',
            defaultMessage: 'Medicine (MD)',
          }),
          href: '/plans/md/',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.plans-do',
            defaultMessage: 'Medicine (DO)',
          }),
          href: '/plans/do/',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.plans-np',
            defaultMessage: 'Nurse Practitioner',
          }),
          href: '/plans/np/',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.plans-pa',
            defaultMessage: 'Physician Assistant',
          }),
          href: '/plans/pa/',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.plans-rn',
            defaultMessage: 'Registered Nurse',
          }),
          href: '/plans/rn/',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.plans-lpn',
            defaultMessage: 'Licensed Practical Nursing',
          }),
          href: '/plans/lpn/',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.plans-dn',
            defaultMessage: 'Dentistry',
          }),
          href: '/plans/dn/',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.plans-ph',
            defaultMessage: 'Pharmacy',
          }),
          href: '/plans/ph/',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.plans-oh',
            defaultMessage: 'Health Professional',
          }),
          href: '/plans/oh/',
        },
      ],
    },
    {
      items: [
        {
          title: intl.formatMessage({
            id: 'footer.column.header.follow-us',
            defaultMessage: 'Follow us',
          }),
          labels: {
            expand: intl.formatMessage({
              id: 'footer.column.button.expand.follow-us',
              defaultMessage: 'Expand Follow Us',
            }),
            collapse: intl.formatMessage({
              id: 'footer.column.button.collapse.follow-us',
              defaultMessage: 'Collapse Follow Us',
            }),
          },
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.youtube',
            defaultMessage: 'YouTube',
          }),
          href: 'https://www.youtube.com/osmosis',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.linkedin',
            defaultMessage: 'LinkedIn',
          }),
          href: 'https://www.linkedin.com/company/osmosis-from-elsevier',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.facebook',
            defaultMessage: 'Facebook',
          }),
          href: 'https://www.facebook.com/OsmoseIt',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.instagram-medical',
            defaultMessage: 'Instagram (Medical)',
          }),
          href: 'https://www.instagram.com/osmosismed',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.instagram-nursing',
            defaultMessage: 'Instagram (Nursing)',
          }),
          href: 'https://www.instagram.com/osmosisnursing/',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.bluesky',
            defaultMessage: 'Bluesky',
          }),
          href: 'https://bsky.app/profile/osmosismed.bsky.social',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.twitter',
            defaultMessage: 'Twitter',
          }),
          href: 'https://twitter.com/OsmosisMed',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.tiktok',
            defaultMessage: 'TikTok',
          }),
          href: 'https://www.tiktok.com/@learnbyosmosis',
        },
      ],
    },
    // Row 2
    {
      items: [
        {
          title: intl.formatMessage({
            id: 'footer.column.header.company',
            defaultMessage: 'Company',
          }),
          labels: {
            expand: intl.formatMessage({
              id: 'footer.column.button.expand.company',
              defaultMessage: 'Expand Company',
            }),
            collapse: intl.formatMessage({
              id: 'footer.column.button.collapse.company',
              defaultMessage: 'Collapse Company',
            }),
          },
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.about-us',
            defaultMessage: 'About Us',
          }),
          href: '/company/',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.careers',
            defaultMessage: 'Careers',
          }),
          href: '/careers',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.press',
            defaultMessage: 'Press',
          }),
          href: 'https://www.elsevier.com/about/global-press-office/media-contacts',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.contact-us',
            defaultMessage: 'Contact us',
          }),
          href: '/contact/',
        },
      ],
    },
    {
      items: [
        {
          title: intl.formatMessage({
            id: 'footer.column.header.resources',
            defaultMessage: 'Resources',
          }),
          labels: {
            expand: intl.formatMessage({
              id: 'footer.column.button.expand.resources',
              defaultMessage: 'Expand Resources',
            }),
            collapse: intl.formatMessage({
              id: 'footer.column.button.collapse.resources',
              defaultMessage: 'Collapse Resources',
            }),
          },
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.library',
            defaultMessage: 'Library',
          }),
          href: '/library/full',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.blog',
            defaultMessage: 'Blog',
          }),
          href: '/blog',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.events',
            defaultMessage: 'Events',
          }),
          href: '/events',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.podcast',
            defaultMessage: 'Podcast',
          }),
          href: '/podcast',
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.help-center',
            defaultMessage: 'Help Center',
          }),
          href: 'https://www.elsevier.support/osmosis',
        },
      ],
    },
    {
      items: [
        {
          title: intl.formatMessage({
            id: 'footer.column.header.institutions',
            defaultMessage: 'For institutions',
          }),
          labels: {
            expand: intl.formatMessage({
              id: 'footer.column.button.expand.institutions',
              defaultMessage: 'Expand Institutions',
            }),
            collapse: intl.formatMessage({
              id: 'footer.column.button.collapse.institutions',
              defaultMessage: 'Collapse Institutions',
            }),
          },
        },
        {
          title: intl.formatMessage({
            id: 'footer.link.institutions',
            defaultMessage: 'Institutions',
          }),
          href: 'https://educators.osmosis.org/',
        },
      ],
    },
  ],
});
